import { useState } from "preact/hooks";
import { appEngine, timeStamp, type DeletedRecord } from "../sync";
import { useRecentlyDeleted, useSyncSession } from "./sync-status";

/**
 * Recently Deleted (issue #26, CONTEXT.md): every Character deleted from the Account, on any
 * Device, with where and when it went and how long is left of the thirty days, each restorable
 * on every Device.
 *
 * Only Characters are listed: a Bookmark or a Recent that went is not something anyone looks for
 * in a trash. A Device that is not signed in has no list at all — its deletions are immediate,
 * which the sign-in card says.
 */
export function RecentlyDeleted() {
  const session = useSyncSession();
  const rows = useRecentlyDeleted().filter((row) => row.collection === "character");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  /** Null until the person opens or closes it themselves; until then it follows the list. */
  const [open, setOpen] = useState<boolean | null>(null);

  if (!session) return null;

  const restore = (row: DeletedRecord) => {
    setBusy(true);
    setMessage(undefined);
    appEngine()
      .restore([{ collection: row.collection, id: row.id }])
      .then(
        (outcome) =>
          setMessage(
            outcome.restored.length > 0
              ? `Restored “${row.name}”. It comes back on every device at their next sync.`
              : `“${row.name}” is no longer on the Account: a deletion is kept for thirty days only.`,
          ),
        (e: unknown) => setMessage(`Could not restore “${row.name}”: ${(e as Error).message}`),
      )
      .finally(() => setBusy(false));
  };

  return (
    <section id="recently-deleted" class="recently-deleted">
      {/* The message sits outside, so restoring the last row does not close it out of sight. */}
      {message && <p class="notice" role="status">{message}</p>}
      <details open={open ?? rows.length > 0} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>
          Recently Deleted{rows.length > 0 ? <> <span class="tag">{rows.length}</span></> : null}
        </summary>
        {rows.length === 0 ? (
          <p class="muted">Nothing deleted in the last 30 days. A Character deleted on any device can be restored here for 30 days.</p>
        ) : (
          <ul class="deleted-rows">
            {rows.map((row) => (
              <li key={row.id} class="card deleted-row">
                <span class="deleted-main">
                  <strong>{row.name}</strong>
                  <span class="muted">
                    {[`Deleted${row.device ? ` on ${row.device}` : ""} ${timeStamp(row.at)}`, row.restorable ? `${row.daysLeft} ${row.daysLeft === 1 ? "day" : "days"} left` : "gone from the Account"].join(" · ")}
                  </span>
                </span>
                <span class="actions">
                  <button type="button" disabled={busy || !row.restorable} onClick={() => restore(row)}>Restore</button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </details>
    </section>
  );
}
